<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { fetchPublicLotteryResultRequest } from '../services/publicLotteryService.js'
import {
  isPublicResultVisible,
  sanitizePublicDrawPayload,
  toPublicErrorMessage,
  toPublicSupportReference,
  toPublicStatusLabel,
} from '../utils/publicUi.js'

const route = useRoute()
const loading = ref(false)
const error = ref('')
const warning = ref('')
const supportReference = ref('')
const draw = ref(null)

const isVisible = computed(() => isPublicResultVisible(draw.value?.status))
const statusLabel = computed(() => toPublicStatusLabel(draw.value?.status))

const load = async () => {
  loading.value = true
  error.value = ''
  warning.value = ''
  supportReference.value = ''
  draw.value = null

  try {
    const data = await fetchPublicLotteryResultRequest(route.params.id)
    draw.value = sanitizePublicDrawPayload(data)

    if (!isVisible.value) {
      warning.value = draw.value?.status === 'archived'
        ? 'This lottery is archived and shown as historical result.'
        : 'Draw results are not available yet for this lottery.'
    }

    if (Number(draw.value?.integrity?.totalTickets || 0) <= 0) {
      warning.value = warning.value || 'Result integrity information is still being prepared.'
    }
  } catch (err) {
    error.value = toPublicErrorMessage(err, 'Public result page is currently unavailable.')
    supportReference.value = toPublicSupportReference(err)
  } finally {
    loading.value = false
  }
}

onMounted(load)
watch(() => route.params.id, load)
</script>

<template>
  <section class="public-result-view">
    <header class="header-row">
      <div>
        <h1>Public Draw Result</h1>
        <p class="muted">Read-only winner and draw summary.</p>
      </div>
      <button class="btn" :disabled="loading" @click="load">{{ loading ? 'Refreshing...' : 'Retry' }}</button>
    </header>

    <p v-if="error" class="error">{{ error }}</p>
    <p v-if="warning" class="warning">{{ warning }}</p>
    <p v-if="supportReference" class="warning">Support reference: {{ supportReference }}</p>

    <article v-if="draw" class="panel">
      <h2>{{ draw.lotteryName }}</h2>
      <p><strong>Status:</strong> {{ statusLabel }}</p>
      <p><strong>Draw timestamp:</strong> {{ draw.drawTimestamp || 'Not available yet' }}</p>
      <p><strong>Prize overview:</strong> {{ draw.prizeOverview }}</p>
      <p><strong>Payment visibility:</strong> {{ draw.paymentStatusLabel }}</p>
      <p v-if="draw.paymentMessage" class="muted">{{ draw.paymentMessage }}</p>
      <p><strong>Total tickets:</strong> {{ draw.integrity.totalTickets || 'Unknown' }}</p>
      <p><strong>Winner count:</strong> {{ draw.integrity.winnersCount || 0 }}</p>
    </article>

    <section v-if="draw && draw.winners.length" class="panel">
      <h3>Winner Summary</h3>
      <ul class="winner-list">
        <li v-for="winner in draw.winners" :key="winner.key">
          <strong>{{ winner.label }}</strong> - Ticket {{ winner.ticket }}
        </li>
      </ul>
    </section>

    <p v-else-if="draw" class="muted">No public winners published yet.</p>
  </section>
</template>

<style scoped>
.public-result-view { max-width: 760px; margin: 0 auto; padding: 1rem; display: grid; gap: 0.75rem; }
.header-row { display: flex; justify-content: space-between; align-items: flex-start; gap: 1rem; }
.panel { border: 1px solid #e5e7eb; border-radius: 8px; background: #fff; padding: 0.75rem; }
.winner-list { margin: 0; padding-left: 1rem; color: #374151; }
.btn { border: none; border-radius: 6px; background: #2563eb; color: #fff; padding: 0.45rem 0.75rem; min-height: 44px; }
.muted { color: #6b7280; }
.warning { color: #92400e; background: #fffbeb; border-radius: 6px; padding: 0.45rem; }
.error { color: #991b1b; background: #fee2e2; border-radius: 6px; padding: 0.45rem; }
@media (max-width: 640px) { .header-row { flex-direction: column; } }
</style>

